import { useCallback, useMemo, useRef } from 'react';

import type { ColumnSizingInfoState, ColumnSizingState, Table } from '@tanstack/react-table';

import { token } from '@atlaskit/tokens';

import { TableType } from '../charlie-table.types';

type UseColumnCssVarsParams<T> = {
	table: Table<T>;
	columnSizing: ColumnSizingState;
	columnSizingInfo: ColumnSizingInfoState;
	tableType: TableType;
	tableRef: React.RefObject<HTMLTableElement>;
};

type UseColumnCssVarsReturn = {
	columnCssVars: Record<string, string | undefined>;
	columnSizeVars: Record<string, string | undefined>;
	columnResizeVars: Record<string, string | undefined>;
	handleResizeHandleHoverChange: (columnId: string | false) => void;
};

/**
 * Custom hook that manages all CSS custom properties for table columns.
 * Handles pinning, sizing, and resize state with performance optimizations.
 */
export function useColumnCssVars<T>({
	table,
	columnSizing,
	columnSizingInfo,
	tableType,
	tableRef,
}: UseColumnCssVarsParams<T>): UseColumnCssVarsReturn {
	const hoveredResizeColumnIdRef = useRef<string | false>(false);

	// CSS vars for pinned columns positioning
	const columnCssVars = useMemo(() => {
		const cols = table.getAllLeafColumns();
		const colCss: Record<string, string | undefined> = {};
		cols.forEach((col) => {
			const isPinned = col.getIsPinned();
			if (!isPinned || !col) {
				return;
			}

			const colKeyPrefix = `--col-${col.id}`;

			colCss[`${colKeyPrefix}-left`] =
				isPinned === 'left' ? `${col.getStart('left')}px` : undefined;
			colCss[`${colKeyPrefix}-right`] =
				isPinned === 'right' ? `${col.getAfter('right')}px` : undefined;
			colCss[`${colKeyPrefix}-zIndex`] = '200';
			colCss[`${colKeyPrefix}-position`] = 'sticky';
		});
		if (
			(table?.getState()?.columnPinning?.left && table.getState().columnPinning.left!.length > 0) ||
			(table?.getState()?.columnPinning?.right && table.getState().columnPinning.right!.length > 0)
		) {
			colCss[`--col-group-position`] = 'sticky';
		}
		return colCss;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [table.options.state]);

	// CSS vars for column sizes
	const columnSizeVars = useMemo(() => {
		const cols = table.getAllLeafColumns();
		const colCss: Record<string, string | undefined> = {};
		cols.forEach((col) => {
			colCss[`--col-${col.id}-size`] = `${col.getSize()}px`;
		});
		return colCss;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [table.options.state, columnSizing]);

	// CSS vars for resize state (only for actively resizing column)
	const columnResizeVars = useMemo(() => {
		const colCss: Record<string, string | undefined> = {};
		const resizingColumnId = columnSizingInfo.isResizingColumn;

		// Only set CSS vars for the column being actively resized (not hover)
		// Hover is handled via direct DOM manipulation to avoid re-renders
		if (resizingColumnId) {
			colCss[`--col-${resizingColumnId}-resizing-border-block-end-color`] = 'transparent';
			colCss[`--col-${resizingColumnId}-resizing-border-width`] = '2px';
			colCss[`--col-${resizingColumnId}-resizing-border-color`] = token('color.border.brand');
			// Adjust resize handle position to compensate for border width change
			// Input tables: 1px → 2px = -1px offset
			// Inventory tables: 0px → 2px = -2px offset
			const offsetPx = tableType === TableType.input ? '-1px' : '-2px';
			colCss[`--col-${resizingColumnId}-resizer-offset`] = offsetPx;
		}

		return colCss;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [table.options.state, columnSizingInfo, tableType]);

	/**
	 * Handles hover state for resize handles using direct DOM manipulation.
	 * This bypasses React's render cycle, preventing re-renders on every hover.
	 * The border and position offset are applied directly to the table element's style.
	 */
	const handleResizeHandleHoverChange = useCallback(
		(columnId: string | false) => {
			const tableEl = tableRef.current;
			if (!tableEl) {
				return;
			}

			// Clear previous hover styles
			const prevId = hoveredResizeColumnIdRef.current;
			if (prevId) {
				tableEl.style.removeProperty(`--col-${prevId}-hover-border-width`);
				tableEl.style.removeProperty(`--col-${prevId}-hover-border-color`);
				tableEl.style.removeProperty(`--col-${prevId}-resizer-offset`);
			}

			// Set new hover styles
			if (columnId) {
				tableEl.style.setProperty(`--col-${columnId}-hover-border-width`, '2px');
				tableEl.style.setProperty(`--col-${columnId}-hover-border-color`, token('color.border'));
				// Compensate for border width change to prevent layout shift
				const offsetPx = tableType === TableType.input ? '-1px' : '-2px';
				tableEl.style.setProperty(`--col-${columnId}-resizer-offset`, offsetPx);
			}

			hoveredResizeColumnIdRef.current = columnId;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[tableType],
	);

	return {
		columnCssVars,
		columnSizeVars,
		columnResizeVars,
		handleResizeHandleHoverChange,
	};
}
